import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import Bugsnag from '@bugsnag/js';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { initializeStart } from '../initialize/initialize.actions';
import { environmentLoadedFailure, environmentLoadedSuccess } from './environment.actions';
import { EnvironmentResponse } from './environment.models';

const appVersion = require('../../../../package.json').version;
const PRODUCTION_ENV_NAME = 'production';

let releaseStage = PRODUCTION_ENV_NAME;

Bugsnag.start({
	apiKey: 'e5136529d7933d2a6f2a6dffe08f6f06',
	appVersion,
	appType: 'client',
	maxBreadcrumbs: 100,
	maxEvents: 100,
	redactedKeys: [
		'access_token',
		/^password$/i,
		'billing',
		'firstName',
		'middleInitial',
		'lastName',
		'address1',
		'address2',
		'city',
		'zipPostal',
		'email',
	],
	releaseStage,
	onError(event) {
		/**
		 * Our environment release stage is determined asyncronously. This doesn't stop it from reporting on page load,
		 * but if the environment comes back that it's not production, it will after that.
		 * @see {@link: https://github.com/bugsnag/bugsnag-js/issues/813}
		 */
		return releaseStage === PRODUCTION_ENV_NAME;
	},
	metadata: {
		ClientConfig: {
			referrer: document?.referrer,
		},
	},
});

/**
 * Handles sending and receiving the environment data so we know where to send API requests.
 */
@Injectable()
export class EnvironmentEffects {
	/**
	 * Sends the request to our PHP file to grab the environment information.
	 */
	loadEnvironment$ = createEffect(() =>
		this._actions$.pipe(
			ofType(initializeStart),
			mergeMap(() =>
				this._fetchEnvironment().pipe(
					map((env) => {
						/**
						 * If we're not in production, stop bugsnag from reporting
						 * @see {@link: https://github.com/bugsnag/bugsnag-js/issues/813}
						 */
						if (!env.isProduction) {
							releaseStage = 'development';
						}

						return environmentLoadedSuccess({ payload: env });
					}),
					catchError(() => of(environmentLoadedFailure()))
				)
			)
		)
	);

	constructor(private _actions$: Actions, private _http: HttpClient) {}

	/**
	 * Grabs the environment information.
	 * @returns An observable of the environment information.
	 */
	private _fetchEnvironment(): Observable<EnvironmentResponse> {
		// production here is any real build, e.g. not localhost
		return this._http.get<EnvironmentResponse>(`./getEnvironment.php`);
	}
}
